import { useEffect, useState } from 'react';
import Header from '../../components/Header/Header';
import USDRequestTable from '../../components/Table/TableBody/USDRequestTable';
import { Loader } from '@piggytechng/component-library';
import useUSDRequest from '../../hooks/useUSDRequest';
import ModalSearch from '../../components/Modals/ModalSearch';
import ModalFilter from '../../components/Modals/ModalFilter';
import NoData from '../../components/NoData/NoData';
import useRolePermissions from '../../hooks/useRolePermissions';
import WarningIcon from '../../assets/icons/Info.svg';
import ReceiptIcon from '../../assets/icons/pos-reciept.svg';
import CheckListIcon from '../../assets/icons/check-list.svg';
import Skeleton from 'react-loading-skeleton';
import DateFilter from '../../components/Modals/DateFilter';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

const USDRequest = () => {
  const {
    fetchUSDRequests,
    isLoading,
    isLoadingMore,
    isLoadingMetrics,
    canResetSearch,
    canResetFilter,
    setUsdRequestsParams,
    usdRequestsParams,
    isTest,
    viewTodaysRequest,
    setViewTodaysRequest,
    setUSDTimeParams,
    USDTimeParams,
  } = useUSDRequest();
  const [filterModal, setFilterModal] = useState(false);
  const [showTimeFilter, setShowTimeFilter] = useState(false);
  const [searchModal, setSearchModal] = useState(false);
  const { isLoadingRolesAndPermissions, restrictTabViewByRole } = useRolePermissions({});
  const [showDropdown, setShowDropdown] = useState(false);
  const location = useLocation();
  const usdRequestsData = useSelector((state: RootState) => state.usdRequests);
  const scrollDetails = useSelector((state: RootState) => state.usdRequestsScrollUtils);
  const usdRequestsMetricsData = useSelector((state: RootState) => state.usdRequestsMetrics);

  useEffect(() => {
    if (!scrollDetails.isGoBack && scrollDetails.scrollY === 0) {
      fetchUSDRequests();
    }
  }, []);

  const scrollToElement = () => {
    if (location.pathname !== '/usd-requests') return;

    setTimeout(() => {
      return window.scrollTo({
        top: Number(scrollDetails.scrollY) || 0,
        behavior: 'smooth',
      });
    }, 800);
  };

  useEffect(() => {
    if (window) {
      scrollToElement();
    }
  }, [window]);

  useEffect(() => {
    if (USDTimeParams?.start_date?.name === 'All Time') {
      USDTimeParams?.isTimeFilterApplied && fetchUSDRequests(null, false, true);
      return;
    }

    let startDate = dayjs(USDTimeParams?.start_date?.date).utc().toISOString();
    let endDate = dayjs(USDTimeParams?.end_date?.date).add(1, 'day').utc().toISOString();

    if (USDTimeParams?.start_date?.name === 'Today') {
      startDate = dayjs(USDTimeParams?.start_date?.date).utc().toISOString();
      endDate = dayjs(USDTimeParams?.start_date?.date).add(1, 'day').utc().endOf('day').toISOString();
    }

    const obj = {
      limit: 500,
      start: startDate,
      end: endDate,
      page: 1,
      status: usdRequestsParams.status,
      search: usdRequestsParams.search,
    };

    setUSDTimeParams((prev) => ({
      ...prev,
      ...obj,
    }));

    setUsdRequestsParams((prev) => ({
      ...prev,
      limit: 1000,
      start: startDate,
      end: endDate,
    }));

    fetchUSDRequests(obj);
  }, [USDTimeParams?.start_date?.date, USDTimeParams?.end_date?.date]);

  useEffect(() => {
    if (isLoadingRolesAndPermissions) return;
    restrictTabViewByRole('usdRequest', 'view', () => window.history.back());
  }, [isLoadingRolesAndPermissions]);

  const clearFilter = () => {
    setViewTodaysRequest(false);
    fetchUSDRequests(null, false, true);
  };

  return (
    <>
      <Header name="USD Requests" />

      <div className="customers-page usd-home">
        <div className="customers-page__card">
          {isLoading ? (
            <div className="page-loader">
              <Loader color="#0F60D6" />
            </div>
          ) : (
            <>
              <div className="customers-filter">
                <div className="input-field" onClick={() => setSearchModal(true)}>
                  <input
                    type="text"
                    placeholder="Search requests by user ID or email only"
                    value={usdRequestsParams.search}
                  />
                </div>
                <div className="query-filter--2" onClick={() => setShowDropdown?.(!showDropdown)}>
                  <div className="icon">
                    <svg width="10" height="10" viewBox="0 0 18 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M0.75 0.75H17.25M6.75 13.25H11.25M3.75 7H14.25"
                        stroke="#1E293B"
                        strokeWidth="1.5"
                        strokeLinecap="square"
                      />
                    </svg>
                  </div>

                  <DateFilter
                    onClose={() => setShowTimeFilter(false)}
                    show={showTimeFilter}
                    params={USDTimeParams}
                    setParams={setUSDTimeParams}
                    showDropdown={showDropdown}
                    setShowDropdown={setShowDropdown}
                  >
                    {USDTimeParams?.start_date?.name}
                  </DateFilter>
                </div>
                <div className="query-filter" onClick={() => setFilterModal(true)}>
                  <div>
                    <svg width="10" height="10" viewBox="0 0 18 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M0.75 0.75H17.25M6.75 13.25H11.25M3.75 7H14.25"
                        stroke="#1E293B"
                        strokeWidth="1.5"
                        strokeLinecap="square"
                      />
                    </svg>
                  </div>
                  <div className="text">Filter</div>
                </div>

                {(canResetSearch || canResetFilter || viewTodaysRequest) && (
                  <div className="query-filter" onClick={() => clearFilter()}>
                    <div></div>
                    <div className="text">Reset</div>
                  </div>
                )}
              </div>
              {usdRequestsData.documents && usdRequestsData.documents.length === 0 ? (
                <NoData
                  message={canResetFilter || canResetSearch ? 'No USD requests found' : 'No USD requests at the moment'}
                />
              ) : (
                <div>
                  <div className="metric-cards-container">
                    <div className="metric-card">
                      <div className="card-icon">
                        <img src={ReceiptIcon} alt="warning" />
                      </div>
                      <p className="card-title">TOTAL USD REQUEST</p>
                      {isLoadingMetrics && !usdRequestsMetricsData?.total ? (
                        <Skeleton baseColor="#F6F9FB" highlightColor="#e8e8e8" width={70} height={40} />
                      ) : (
                        <h1 className="amount">{usdRequestsMetricsData?.total || 0}</h1>
                      )}
                    </div>
                    <div className="metric-card">
                      <div className="card-icon">
                        <img src={CheckListIcon} alt="warning" />
                      </div>
                      <p className="card-title">APPROVED USD REQUEST</p>
                      {isLoadingMetrics && !usdRequestsMetricsData?.approved ? (
                        <Skeleton baseColor="#F6F9FB" highlightColor="#e8e8e8" width={70} height={40} />
                      ) : (
                        <h1 className="amount">{usdRequestsMetricsData?.approved || 0}</h1>
                      )}
                    </div>
                    <div className="metric-card">
                      <div className="card-icon">
                        <img src={WarningIcon} alt="warning" />
                      </div>
                      <p className="card-title">PENDING USD REQUEST</p>
                      {isLoadingMetrics && !usdRequestsMetricsData?.under_review ? (
                        <Skeleton baseColor="#F6F9FB" highlightColor="#e8e8e8" width={70} height={40} />
                      ) : (
                        <h1 className="amount">{usdRequestsMetricsData?.under_review || 0}</h1>
                      )}
                    </div>
                  </div>
                  <USDRequestTable tableData={usdRequestsData.documents} isCustomers={true} isTest={isTest} />
                </div>
              )}
              {!isLoading &&
                usdRequestsData.documents.length < (usdRequestsData?.total || 0) &&
                !usdRequestsParams.search && (
                  <div className="load-more" onClick={() => fetchUSDRequests(null, true)}>
                    {isLoadingMore ? (
                      <Loader color="#0F60D6" maxWidth="50px" />
                    ) : (
                      <span className="pointer">Show more</span>
                    )}
                  </div>
                )}
            </>
          )}

          <ModalSearch
            modalIsOpen={searchModal}
            searchContext="search-usd-requests"
            data={usdRequestsParams}
            setIsOpen={setSearchModal}
            searchDataBy={(filter, empty) => {
              const params = {
                ...usdRequestsParams,
                start: '',
                end: '',
                status: '',
                ...filter.usdrequests,
              };
              setUsdRequestsParams(params);
              if (!empty) {
                fetchUSDRequests({ ...params, page: 1 });
              }
            }}
          />

          <ModalFilter
            modalIsOpen={filterModal}
            searchContext="filter-usd-requests"
            data={usdRequestsParams}
            setIsOpen={setFilterModal}
            filterDataBy={(filter, empty) => {
              const params = {
                ...usdRequestsParams,
                search: '',
                ...filter.usdrequests,
              };
              setUsdRequestsParams(params);
              if (!empty) {
                fetchUSDRequests({ ...params, page: 1 });
              }
            }}
          />
        </div>
      </div>
    </>
  );
};

export default USDRequest;
